import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Divider,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '964776120413-ei5cuq1l62t8sgid029vbh691kqi3rda.apps.googleusercontent.com';

const PLANS = [
  { key: 'social_basic', name: '社群助手', price: 'NT$ 249/月', desc: '留言自動回覆\n手動私訊回覆' },
  { key: 'social_ai', name: 'AI 社群', price: 'NT$ 499/月', desc: '留言自動回覆\nAI 私訊回覆' },
  { key: 'business', name: '商務方案', price: '客製報價', desc: '全平台整合\nLINE + Meta' }
];

const PLAN_DETAILS = {
  social_basic: [
    { label: '自動回覆次數', value: '無限' },
    { label: '回覆規則', value: '5 則' },
    { label: '粉專數量', value: '1 個' },
    { label: '私訊收件匣', value: '✓ 手動回覆', color: 'success.main' },
    { label: 'FAQ 知識庫', value: '—', color: 'text.disabled' }
  ],
  social_ai: [
    { label: '自動回覆次數', value: '無限' },
    { label: '回覆規則', value: '20 則' },
    { label: '粉專數量', value: '1 個' },
    { label: '私訊收件匣', value: '✓ AI 自動回覆', color: 'success.main' },
    { label: 'FAQ 知識庫', value: '30 則' }
  ]
};

const BUSINESS_FEATURES = [
  'FB / IG 留言自動回覆', 'LINE 官方帳號整合',
  'AI 私訊自動回覆', 'Web Chat 網站客服',
  'FAQ 知識庫（100 則起）', 'AI 每日總覽報表',
  '對話流程自動化', '5 位員工帳號',
  '無限粉專 / 回覆規則', '專人導入 + 技術支援'
];

const RegisterPage = () => {
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState('social_ai');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registered, setRegistered] = useState(false);

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  const isBusiness = selectedPlan === 'business';
  const isSocialPlan = selectedPlan.startsWith('social_');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') setEmailError('');
    if (name === 'password') setPasswordError('');
    if (name === 'confirmPassword') setConfirmPasswordError('');
    setError('');
  };

  const validateForm = () => {
    let isValid = true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email.trim()) {
      setEmailError('請輸入電子郵件');
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      setEmailError('請輸入有效的電子郵件地址');
      isValid = false;
    }
    if (!formData.password) {
      setPasswordError('請輸入密碼');
      isValid = false;
    } else if (formData.password.length < 6) {
      setPasswordError('密碼必須至少6個字符');
      isValid = false;
    }
    if (!formData.confirmPassword) {
      setConfirmPasswordError('請確認密碼');
      isValid = false;
    } else if (formData.confirmPassword !== formData.password) {
      setConfirmPasswordError('密碼不匹配');
      isValid = false;
    }
    return isValid;
  };

  const saveAuthAndRedirect = (data) => {
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    if (data.user?.companyId) localStorage.setItem('companyId', data.user.companyId.toString());
    if (data.companyStatus) localStorage.setItem('companyStatus', data.companyStatus);
    if (data.planName) localStorage.setItem('planName', data.planName);
    if (data.trialEndsAt) localStorage.setItem('trialEndsAt', data.trialEndsAt);
    window.dispatchEvent(new Event('authChange'));

    const planName = data.planName || '';
    if (planName.startsWith('social_')) {
      window.location.href = `${process.env.REACT_APP_API_URL}/settings/meta`;
    } else {
      setRegistered(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError('');
    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const username = formData.email.split('@')[0];
      const response = await fetch(`${apiUrl}/api/auth/register-company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          companyName: username,
          username,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
          selectedPlan
        }),
        mode: 'cors'
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setSnackbarSeverity('success');
        setSnackbarMessage(isSocialPlan ? '帳號已啟用！' : '公司註冊成功，請等待審核');
        setOpenSnackbar(true);
        if (isSocialPlan && data.token) {
          saveAuthAndRedirect(data);
        } else {
          setRegistered(true);
        }
      } else {
        const msg = data.message || '註冊時發生錯誤';
        setError(msg);
        setSnackbarSeverity('error');
        setSnackbarMessage(msg);
        setOpenSnackbar(true);
      }
    } catch (err) {
      const msg = err.message?.includes('fetch') ? '無法連接到伺服器' : '註冊時發生錯誤';
      setError(msg);
      setSnackbarSeverity('error');
      setSnackbarMessage(msg);
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleRegister = async (credentialResponse) => {
    if (!credentialResponse.credential) return;
    setIsLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential, selectedPlan }),
        mode: 'cors'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '註冊失敗');
      }

      const data = await response.json();
      setSnackbarSeverity('success');
      const planName = data.planName || '';
      setSnackbarMessage(planName.startsWith('social_') ? '帳號已啟用！' : '註冊成功！請等待審核。');
      setOpenSnackbar(true);

      if (planName.startsWith('social_') && data.token) {
        saveAuthAndRedirect(data);
      } else {
        setRegistered(true);
      }
    } catch (err) {
      setError(err.message || 'Google 註冊失敗');
      setSnackbarSeverity('error');
      setSnackbarMessage(err.message || 'Google 註冊失敗');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 註冊成功（商務方案）— 等待審核
  if (registered) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              {isBusiness ? '註冊成功' : '帳號已建立'}
            </Typography>
            <Alert severity="info" sx={{ width: '100%', mb: 3 }}>
              {isBusiness
                ? '您的公司帳號已建立，我們將在 2 個工作天內與您聯繫。'
                : '您的帳號已建立，目前為待審核狀態。管理員審核通過後，您將收到通知信件。'}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/login')}
              sx={{ bgcolor: '#145da0', '&:hover': { bgcolor: '#125390' } }}>
              返回登入頁
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', borderRadius: 2 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
            開始使用
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            選擇方案，立即開始
          </Typography>

          {/* 方案選擇 */}
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%', mb: 1 }}>
            {PLANS.map(plan => (
              <Box
                key={plan.key}
                onClick={() => setSelectedPlan(plan.key)}
                sx={{
                  flex: 1,
                  border: 2,
                  borderColor: selectedPlan === plan.key ? 'primary.main' : '#e0e0e0',
                  borderRadius: 2.5,
                  p: 2,
                  cursor: 'pointer',
                  textAlign: 'center',
                  bgcolor: selectedPlan === plan.key ? 'primary.50' : 'transparent',
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': { borderColor: selectedPlan === plan.key ? 'primary.main' : '#90caf9' }
                }}
              >
                {selectedPlan === plan.key && (
                  <Box sx={{
                    position: 'absolute', top: 6, right: 6,
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: 'primary.main', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700
                  }}>✓</Box>
                )}
                <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{plan.name}</Typography>
                <Typography sx={{ fontSize: 19, color: 'primary.main', fontWeight: 700, mb: 0.5 }}>{plan.price}</Typography>
                <Typography sx={{ fontSize: 16, color: '#333', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                  {plan.desc}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* 方案細節 — 社群方案 */}
          {!isBusiness && PLAN_DETAILS[selectedPlan] && (
            <Box sx={{ width: '100%', bgcolor: '#f5f8ff', border: '1px solid #e3ecf6', borderRadius: 2, p: 2, mb: 1 }}>
              {PLAN_DETAILS[selectedPlan].map((row, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography sx={{ fontSize: 15, color: '#555' }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: row.color || '#222' }}>{row.value}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* 商務方案細節 */}
          {isBusiness && (
            <Box sx={{ width: '100%', bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, p: 2, mb: 1 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#e65100', mb: 1 }}>商務方案包含</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px' }}>
                {BUSINESS_FEATURES.map((f, i) => (
                  <Typography key={i} sx={{ fontSize: 15, color: '#333', lineHeight: 1.8 }}>✓ {f}</Typography>
                ))}
              </Box>
              <Typography sx={{ fontSize: 15, color: '#e65100', fontWeight: 500, textAlign: 'center', mt: 1.5 }}>
                📧 註冊後我們將在 2 個工作天內與您聯繫
              </Typography>
            </Box>
          )}

          <Typography variant="caption" sx={{
            color: isBusiness ? '#e65100' : 'success.main',
            fontWeight: 500, mb: 2, fontSize: 13
          }}>
            {isBusiness ? '' : '🎉 免費試用 14 天，不用綁信用卡'}
          </Typography>

          {/* Google 註冊 */}
          <Box sx={{ width: '100%', mb: 1, display: 'flex', justifyContent: 'center' }}>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleRegister}
                onError={() => { setError('Google 註冊失敗'); }}
                text="signup_with"
                locale="zh_TW"
                theme="outline"
                size="large"
                shape="rectangular"
                width="400"
              />
            )}
          </Box>

          <Divider sx={{ width: '100%', my: 1.5 }}>
            <Typography variant="caption" color="text.secondary">或</Typography>
          </Divider>

          {/* Email 展開按鈕 */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setShowEmailForm(!showEmailForm)}
            startIcon={showEmailForm ? <KeyboardArrowUpIcon /> : <MailOutlineIcon />}
            sx={{
              py: 1.3,
              fontSize: 15,
              fontWeight: 500,
              color: '#555',
              borderColor: '#bbb',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
            }}
          >
            {showEmailForm ? '收起' : '使用 Email 註冊'}
          </Button>

          {/* Email 表單 */}
          <Collapse in={showEmailForm} sx={{ width: '100%' }}>
            {error && (
              <Alert severity="error" sx={{ width: '100%', mt: 2, mb: 1 }}>{error}</Alert>
            )}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 2 }}>
              <TextField
                margin="normal" required fullWidth
                id="email" label="電子郵件" name="email"
                autoComplete="email" type="email"
                value={formData.email} onChange={handleChange}
                error={!!emailError} helperText={emailError}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal" fullWidth
                id="phone" label="聯絡電話（選填）" name="phone"
                autoComplete="tel"
                value={formData.phone} onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal" required fullWidth
                name="password" label="密碼"
                type={showPassword ? 'text' : 'password'}
                id="password" autoComplete="new-password"
                value={formData.password} onChange={handleChange}
                error={!!passwordError}
                helperText={passwordError || '密碼必須至少6個字符'}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                margin="normal" required fullWidth
                name="confirmPassword" label="確認密碼"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword" autoComplete="new-password"
                value={formData.confirmPassword} onChange={handleChange}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                type="submit" fullWidth variant="contained" disabled={isLoading}
                sx={{ py: 1.5, bgcolor: '#145da0', '&:hover': { bgcolor: '#125390' }, '&:disabled': { bgcolor: '#ccc' } }}
              >
                {isLoading ? (
                  <><CircularProgress size={20} sx={{ mr: 1, color: 'white' }} /> 註冊中...</>
                ) : (
                  isBusiness ? '註冊並等待聯繫' : '免費試用'
                )}
              </Button>
            </Box>
          </Collapse>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2">
              已有帳號？{' '}
              <Link href="/login" variant="body2" underline="hover">登入</Link>
            </Typography>
          </Box>
        </Paper>
      </Container>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </GoogleOAuthProvider>
  );
};

export default RegisterPage;
